/**
 * @file 채널 좌측 사이드바 — 채널 목록 + <b>선택된 채널의 폴더 트리</b>.
 *
 * <h3>왜 파일을 따로 뺐나 — 애니메이션이 재생되지 않던 원인</h3>
 * <p>예전에는 {@code ChannelItem}·{@code FolderTree} 같은 하위 컴포넌트를 사이드바 <b>렌더 본문
 * 안에서</b> 정의했다. 그러면 부모가 리렌더될 때마다 컴포넌트 <i>타입</i>이 새로 만들어져 React 가
 * 서브트리를 통째로 unmount→mount 한다. 결과는 두 가지다:
 * <ol>
 *   <li><b>접기 애니메이션이 아예 재생되지 않는다</b> — 새로 mount 된 노드는 이미 최종 상태다.</li>
 *   <li>⋮ 메뉴 anchor 같은 지역 state 가 날아간다.</li>
 * </ol>
 * 메일 사이드바({@code MailPage.jsx} 의 {@code CustomFolderItem})가 같은 이유로 모듈 최상위에
 * 정의돼 있고 주석으로 경고해 뒀는데, 채널 쪽에서 그대로 밟았다. 이제 <b>모두 모듈 최상위</b>이며
 * 필요한 값은 {@code ctx} 하나로 받는다.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box, Divider, IconButton, Menu, MenuItem, Stack, Tooltip, Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import CreateNewFolderRoundedIcon from "@mui/icons-material/CreateNewFolderRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import FolderSpecialRoundedIcon from "@mui/icons-material/FolderSpecialRounded";
import SubdirectoryArrowRightRoundedIcon from "@mui/icons-material/SubdirectoryArrowRightRounded";
import ActionButton from "../../component/common/ActionButton";
import { ds } from "../../styles/designSystem";
import { ALL, UNFILED } from "./folder/folderScope";
import { daysUntil, ddayLabel, fmtDate, toLocalDate } from "./dday";
import { projectPhase } from "./channelPhase";

const C = ds.colors;

/**
 * 접기/펼치기 애니메이션 래퍼 — grid-template-rows 0fr↔1fr.
 *
 * <p>MUI {@code Collapse} 를 쓰지 않는 이유는 메일 사이드바와 같다: Collapse 는 자기 콘텐츠 높이를
 * <b>재서</b> 인라인 height 로 박아 넣는다. 사이드바처럼 중첩되면(구간 → 채널 → 폴더) 바깥이 높이를
 * 재는 시점에 안쪽이 아직 0 이라 실제보다 작은 값으로 굳고, 애니메이션이 끝나 overflow:visible 이
 * 되는 순간 하위 항목이 아래 섹션 위로 새어 나온다. 이 방식은 높이를 재지 않아 안전하다.
 */
const CollapseRows = ({ open, children }) => (
    <Box sx={{
        display: "grid",
        gridTemplateRows: open ? "1fr" : "0fr",
        transition: "grid-template-rows 180ms ease",
        flexShrink: 0,
    }}>
        <Box sx={{ overflow: "hidden", minHeight: 0 }}>{children}</Box>
    </Box>
);

/**
 * 최상위 유형(즐겨찾기 / 프로젝트 / 기타) 사이 경계.
 *
 * <p>구간 사이 선({@code borderLight})보다 <b>진하고 여백도 두 배</b>다. 두 종류의 선이
 * 같은 두께·같은 간격이면 "프로젝트 안의 종료" 와 "프로젝트 다음의 기타" 가 같은 층으로 읽혀
 * 채널 유형 구분이 사라진다. 여기서 한 단계 크게 끊어 준다.
 */
/**
 * 최상위 유형(즐겨찾기 / 프로젝트 / 기타) 사이 경계.
 *
 * <p>구간 사이 선({@code borderLight})보다 <b>진하고 여백도 두 배</b>다. 두 종류의 선이
 * 같은 두께·같은 간격이면 "프로젝트 안의 종료" 와 "프로젝트 다음의 기타" 가 같은 층으로 읽혀
 * 채널 유형 구분이 사라진다. 여기서 한 단계 크게 끊어 준다.
 */
const TypeBoundary = () => (
    <Divider sx={{ mx: 2, mt: 3, mb: 2, borderColor: C.border }} />
);

/**
 * 섹션 헤더 — 라벨은 <b>가장 왼쪽</b>, 토글(꺾쇠)은 <b>가장 오른쪽</b>.
 *
 * <p>예전엔 꺾쇠가 라벨 앞에 붙어 헤더가 그 폭만큼 밀려났고, 정작 그 아래 항목은 덜 들여써져
 * <b>부모가 자식보다 더 안쪽</b>에 있는 역전이 생겼다(읽을 때 계층이 뒤집혀 보인다).
 * 토글을 오른쪽 끝으로 빼면 라벨의 왼쪽 기준선이 고정되고, 들여쓰기는 아래 항목만 갖는다.
 */
const SectionHeader = ({ label, count, toggle, expanded }) => (
    <Box
        onClick={toggle}
        sx={{
            display: "flex", alignItems: "center", gap: 0.75, px: 2, pt: 2, pb: 0.5,
            cursor: toggle ? "pointer" : "default",
            "&:hover": toggle ? { opacity: 0.75 } : {},
        }}
    >
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: "0.5px",
            textTransform: "uppercase", flex: 1, minWidth: 0 }}>
            {label}
        </Typography>
        {count != null && (
            <Typography sx={{ fontSize: 11, color: "#cbd5e1", fontWeight: 600 }}>{count}</Typography>
        )}
        {toggle && (
            // 아이콘을 갈아치우지 않고 <b>회전</b>시킨다 — 교체는 순간이동처럼 보여
            // 아래 목록의 열림 애니메이션과 따로 놀았다.
            <ExpandLessRoundedIcon sx={{
                fontSize: 14, color: C.textMuted,
                transition: "transform 160ms ease",
                transform: expanded ? "none" : "rotate(180deg)",
            }} />
        )}
    </Box>
);

/**
 * 폴더 한 줄 — 메일 사이드바의 하위 폴더 행과 같은 모양(↳ + 폴더 아이콘 + 이름 + 건수).
 *
 * <p><b>중첩은 없다.</b> 폴더 안에 폴더를 만들지 않기로 했으므로 depth 개념 자체를 두지 않았다
 * (메일은 재귀 렌더지만 여기는 평면 목록이다). 나중에 중첩이 필요해지면 그건 "폴더가 아니라
 * 채널이 나뉘어야 한다" 는 신호로 본다.
 */
const FolderRow = ({ id, name, count, color, icon, tooltip, ctx }) => {
    const { selectedFolder, onSelectFolder } = ctx;
    const active = selectedFolder === id;
    const row = (
        <Box
            onClick={(e) => { e.stopPropagation(); onSelectFolder(id); }}
            sx={{
                display: "flex", alignItems: "center", gap: 0.5,
                pl: 3.2, pr: 1, py: 0.45, mx: 0.5, borderRadius: 1.2, cursor: "pointer",
                bgcolor: active ? C.primaryBg : "transparent",
                color: active ? C.primaryText : "#475569",
                fontWeight: active ? 700 : 500,
                "&:hover": { bgcolor: active ? C.primaryBg : "#f1f5f9" },
                transition: "background-color 0.12s",
            }}
        >
            <SubdirectoryArrowRightRoundedIcon sx={{ fontSize: 13, color: "#cbd5e1", flexShrink: 0 }} />
            {icon ?? <FolderRoundedIcon sx={{ fontSize: 15, color, flexShrink: 0 }} />}
            <Typography noWrap sx={{ fontSize: 12.5, flex: 1, minWidth: 0, color: "inherit", fontWeight: "inherit" }}>
                {name}
            </Typography>
            {count > 0 && (
                <Typography sx={{ fontSize: 11, color: active ? "inherit" : "#94a3b8", fontWeight: 600 }}>
                    {count}
                </Typography>
            )}
        </Box>
    );
    return tooltip ? <Tooltip title={tooltip} placement="right">{row}</Tooltip> : row;
};

/**
 * 선택된 채널 아래에 붙는 폴더 목록.
 *
 * <p>선택된 채널에만 편다 — 채널마다 폴더를 다 펼치면 사이드바가 순식간에 스크롤 지옥이 되고,
 * 애초에 다른 채널의 폴더를 여기서 고를 일이 없다(고르는 순간 그 채널로 들어가야 하므로).
 */
const FolderTree = ({ channel, ctx }) => {
    const { selectedId, folderData, collapsedFolders, closingChannelId } = ctx;

    // 선택되지 않은 채널 아래에는 아무것도 두지 않는다(사이드바가 순식간에 길어진다).
    if (channel.id !== selectedId) return null;

    /*
     * <b>이 폴더 데이터가 정말 이 채널 것인지.</b>
     *
     * 채널을 바꾸면 selectedId 는 즉시 새 채널이 되지만 folderData 는 조회가 끝날 때까지
     * 이전 채널 것이다. 이 확인이 없으면 새 채널 행 아래에 남의 폴더 목록이 한순간 그려졌다
     * 사라진다 — "잠시 보였다가 사라지는" 깜빡임의 원인이었다.
     */
    const ready = folderData.channelId === channel.id;
    const folders = ready ? folderData.folders : [];
    const unfiledFeedCount = ready ? folderData.unfiledFeedCount : 0;

    /*
     * <b>데이터가 없을 때도 껍데기는 남긴다</b>(return null 하지 않는다).
     *
     * 예전엔 데이터가 오기 전 null 을 반환했다. 그러면 목록이 도착하는 순간 CollapseRows 가
     * <b>이미 열린 상태로 mount</b> 되어 열림 애니메이션이 재생되지 않는다(다른 채널에 갔다
     * 돌아왔을 때 툭 나타나던 이유). 껍데기를 먼저 닫힌 채로 붙여 두면 데이터 도착 시
     * 0fr → 1fr 전이가 실제로 일어난다.
     */
    const open = ready && folders.length > 0
        && !collapsedFolders.has(channel.id) && closingChannelId !== channel.id;

    const totalCount = folders.reduce((n, f) => n + (f.feedCount || 0), 0) + unfiledFeedCount;

    return (
        <CollapseRows open={open}>
            <Box sx={{ pb: 0.5 }}>
                {/*
                  * '전체 폴더' — 이 채널의 피드 전부. 초록 폴더로 다른 폴더(앰버)와 구분한다.
                  *
                  * 배경 강조는 <b>이 목록에서만</b> 준다(채널 행은 굵은 글씨로만 현재 채널을 표시) —
                  * 그래야 파랗게 채워진 줄이 언제나 하나뿐이라 이중 활성으로 보이지 않는다.
                  */}
                {folders.length > 0 && (
                    <FolderRow ctx={ctx} id={ALL} name="전체 폴더" count={totalCount} color="#16a34a"
                        tooltip="이 채널의 모든 폴더 + 미분류" />
                )}
                {folders.map(f => (
                    <FolderRow ctx={ctx} key={f.id} id={f.id} name={f.name} count={f.feedCount} color="#f59e0b"
                        tooltip={f.memberCount === 0
                            ? "참가자 없음 — PM·관리자만 열람"
                            : `참가자 ${f.memberCount}명${f.description ? ` · ${f.description}` : ""}`} />
                ))}
                {/* 미분류는 실제로 있을 때만 — 폴더를 쓰는 채널에도 0건이면 군더더기다. */}
                {folders.length > 0 && unfiledFeedCount > 0 && (
                    <FolderRow ctx={ctx} id={UNFILED} name="미분류" count={unfiledFeedCount} color="#cbd5e1"
                        tooltip="폴더를 지정하지 않은 항목" />
                )}
            </Box>
        </CollapseRows>
    );
};

const ChannelItem = ({ channel, dimmed = false, inFavorites = false, indented = false, ctx }) => {
    // selectedFolder 는 쓰지 않는다 — 폴더 범위 강조는 폴더 목록(FolderRow)이 전담한다.
    const {
        selectedId, hasNewMap, favorites, folderData, collapsedFolders,
        onSelect, onSelectFolder, onManageFolders, toggleFavorite, toggleFolders,
    } = ctx;
    const isCurrent = channel.id === selectedId;
    const loadedFolders = folderData.channelId === channel.id ? folderData.folders : [];
    /**
     * 배경 강조는 <b>화면에서 언제나 한 줄에만</b> 있어야 한다(이중 활성 방지).
     *
     * <p>폴더가 있는 채널이면 범위를 고르는 줄이 폴더 목록(전체 폴더 / 각 폴더 / 미분류)이므로
     * 강조를 그쪽에 양보하고, 채널 행은 <b>굵은 글씨</b>로만 현재 채널임을 표시한다.
     * 폴더가 없는 채널은 채널 행 자체가 유일한 선택지라 그대로 배경을 갖는다.
     */
    const selected = isCurrent && loadedFolders.length === 0;
    const hasNew = hasNewMap?.[channel.id] ?? false;
    const isFav = favorites.has(channel.id);
    /**
     * 폴더 개수 — 배지·토글에 쓴다.
     *
     * <p>두 출처를 쓴다: 목록 API 의 {@code folderCount}(모든 채널) 와, 현재 채널에 대해
     * 이미 받아 둔 폴더 목록 길이. 후자를 우선하는 이유는 (1) 항상 최신이고,
     * (2) {@code folderCount} 는 나중에 추가된 필드라 <b>구버전 백엔드에서는 내려오지 않는다</b> —
     * 그때도 지금 보고 있는 채널의 토글은 동작해야 한다.
     */
    const loadedFolderCount = loadedFolders.length;
    const folderCount = loadedFolderCount || (channel.folderCount ?? 0);
    const foldersOpen = !collapsedFolders.has(channel.id);
    const [hovered, setHovered] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);

    return (
        <Box
            // 채널 행 클릭 = 그 채널의 '전체' 보기. 폴더 선택도 함께 초기화한다 —
            // 이 행이 '전체' 역할을 하므로 눌렀는데 이전 폴더가 남아 있으면 앞뒤가 안 맞는다.
            onClick={() => { onSelect(channel.id); onSelectFolder(ALL); }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            sx={{
                // 행마다 있던 borderBottom 을 없앴다 — 항목이 20개면 회색 줄도 20개가 되어
                // 사이드바가 표처럼 보인다. 구분은 여백(py)과 hover 배경이 이미 충분히 한다.
                //
                // 들여쓰기는 <b>항목에만</b> 준다(헤더는 안 준다) — 그래야 "프로젝트 > 진행중 > 채널"
                // 순으로 왼쪽 기준선이 단계적으로 밀려 계층이 눈으로 읽힌다.
                //
                // 선택 표시는 메일 사이드바와 같은 방식 — 좌측 3px 띠 대신 <b>둥근 배경</b>.
                // 띠는 pane 가장자리에 붙어 있어야 하는 탓에 행을 라운드로 만들 수 없고,
                // 같은 화면 안의 메일 사이드바와 선택 표현이 달라 보였다.
                pl: indented ? 3 : 1.2, pr: 1, py: 0.9,
                mx: 0.5, borderRadius: 1.2, cursor: "pointer",
                bgcolor: selected ? C.primaryBg : "transparent",
                "&:hover": { bgcolor: selected ? C.primaryBg : "#f1f5f9" },
                transition: "background-color 0.12s",
                opacity: dimmed ? 0.5 : 1,
            }}
        >
            <Stack direction="row" alignItems="center" spacing={0.6} mb={0.2}>
                {/*
                  * (제거됨) 별 아이콘 — 즐겨찾기는 ⋮ 메뉴에서 등록·해제한다.
                  *
                  * 채널명 폭이 250px 사이드바에서 이미 빠듯한데 별·개수·⋮·토글 네 개가 붙자
                  * 이름이 "2026 TEL Family ..." 로 잘렸다. 즐겨찾기는 <b>가끔</b> 쓰는 동작이라
                  * 상시 자리를 차지할 이유가 없고, 즐겨찾기된 채널은 위 '즐겨찾기' 섹션에 모여
                  * 보이므로 행에 표식이 없어도 알 수 있다.
                  */}
                <Box flex={1} minWidth={0}>
                    <Typography sx={{
                        fontSize: 13,
                        // 배경은 '전체' 일 때만이지만, 이름 강조는 그 채널을 보고 있는 동안 유지한다.
                        fontWeight: isCurrent ? 700 : 600,
                        color: isCurrent ? C.primaryText : "#1e293b", lineHeight: 1.3,
                    }} noWrap>
                        {channel.name}
                    </Typography>
                </Box>
                {hasNew && !isCurrent && (
                    <Box sx={{
                        width: 8, height: 8, borderRadius: "50%",
                        bgcolor: "#ef4444", flexShrink: 0,
                    }} />
                )}
                {/* 폴더 개수 — 내가 볼 수 있는 폴더 수(서버가 가시성 적용해 내려준다). */}
                {folderCount > 0 && (
                    <Tooltip title={`폴더 ${folderCount}개`}>
                        <Stack direction="row" alignItems="center" spacing={0.2} sx={{ flexShrink: 0 }}>
                            <FolderRoundedIcon sx={{ fontSize: 12, color: "#cbd5e1" }} />
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textMuted }}>
                                {folderCount}
                            </Typography>
                        </Stack>
                    </Tooltip>
                )}
                {/* ⋮ — 채널별 부가 동작(폴더 관리). hover 또는 현재 채널일 때만 보인다.
                    자리는 늘 잡아 둬서 오른쪽 열(토글)이 행마다 어긋나지 않게 한다. */}
                <Box sx={{ width: 18, flexShrink: 0, display: "flex", justifyContent: "center" }}>
                    {(hovered || isCurrent) && (
                        <Box
                            onClick={e => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}
                            sx={{ display: "flex", alignItems: "center",
                                borderRadius: 1, "&:hover": { bgcolor: "rgba(15,23,42,0.06)" } }}
                        >
                            <MoreVertRoundedIcon sx={{ fontSize: 16, color: C.textMuted }} />
                        </Box>
                    )}
                </Box>
                {/* 폴더 접기 토글 — 행 맨 오른쪽. 폴더가 없는 채널도 자리는 남겨 열을 맞춘다. */}
                <Box sx={{ width: 16, flexShrink: 0, display: "flex", justifyContent: "center" }}>
                    {isCurrent && loadedFolderCount > 0 && (
                        <Tooltip title={foldersOpen ? "폴더 접기" : `폴더 ${loadedFolderCount}개 펼치기`}>
                            <IconButton size="small"
                                onClick={e => { e.stopPropagation(); toggleFolders(channel.id); }}
                                sx={{ p: 0 }}>
                                <ExpandLessRoundedIcon sx={{
                                    fontSize: 16, color: C.textMuted,
                                    transition: "transform 160ms ease",
                                    transform: foldersOpen ? "none" : "rotate(180deg)",
                                }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
                <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                    onClick={e => e.stopPropagation()}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                    slotProps={{ paper: { sx: { borderRadius: 1.5, minWidth: 150 } } }}>
                    <MenuItem
                        onClick={() => { setMenuAnchor(null); onManageFolders(channel.id); }}
                        sx={{ fontSize: 13, gap: 1 }}>
                        <CreateNewFolderRoundedIcon sx={{ fontSize: 16 }} /> 폴더 추가·관리
                    </MenuItem>
                    <MenuItem
                        onClick={e => { setMenuAnchor(null); toggleFavorite(e, channel.id); }}
                        sx={{ fontSize: 13, gap: 1 }}>
                        {isFav
                            ? <><StarBorderRoundedIcon sx={{ fontSize: 16 }} /> 즐겨찾기 해제</>
                            : <><StarRoundedIcon sx={{ fontSize: 16, color: "#f59e0b" }} /> 즐겨찾기</>}
                    </MenuItem>
                </Menu>
            </Stack>
            {channel.type === "PROJECT" && (() => {
                /**
                 * 채널명 아래 한 줄 표시.
                 * - 우선 D-N (마감일 기준, 마감일 없으면 시작일까지)
                 * - 옆에 전체 일정: MM.DD ~ MM.DD
                 * - 시작일·마감일 모두 없으면 "일정 미지정" 만
                 */
                // 계산·표기는 dday.js 한 곳에서 — 본문 헤더와 다른 숫자가 나오던 자리.
                const start = toLocalDate(channel.startDate);
                const deadline = toLocalDate(channel.deadlineDate);
                const fmt = fmtDate;

                if (!start && !deadline) {
                    return (
                        <Typography sx={{ fontSize: 11, color: "#94a3b8", ml: inFavorites ? 2.5 : 0 }} noWrap>
                            일정 미지정
                        </Typography>
                    );
                }

                const range = start && deadline
                    ? `${fmt(start)} ~ ${fmt(deadline)}`
                    : start ? `${fmt(start)} ~` : `~ ${fmt(deadline)}`;

                let dday = "";
                if (deadline && !channel.archived) {
                    dday = ddayLabel(deadline) || "";
                } else if (start && !channel.archived) {
                    const diff = daysUntil(start);
                    if (diff > 0) dday = `시작 D-${diff}`;
                }

                return (
                    <Typography sx={{ fontSize: 11, color: "#94a3b8", ml: inFavorites ? 2.5 : 0 }} noWrap>
                        {dday && (<><Box component="span" sx={{ fontWeight: 700 }}>{dday}</Box>{" · "}</>)}
                        {range}
                    </Typography>
                );
            })()}
        </Box>
    );
};

function ChannelSidebar({
    channels, selectedId, onSelect, onCreateOpen, hasNewMap, onManageFolders,
    folderData = { folders: [], unfiledFeedCount: 0, canManage: false },
    selectedFolder, onSelectFolder,
}) {
    const [showOngoingProj, setShowOngoingProj] = useState(true);
    const [showUpcomingProj, setShowUpcomingProj] = useState(true);
    const [showUndatedProj, setShowUndatedProj] = useState(true);
    const [showArchivedProj, setShowArchivedProj] = useState(false);
    const [showFavorites, setShowFavorites] = useState(true);
    /**
     * 폴더 목록을 <b>접은</b> 채널 id 집합.
     *
     * <p>"접힌 것" 을 담는다 — 기본값(빈 집합)이 곧 "전부 펼침" 이라 초기 동작이 자연스럽다.
     * (메일 사이드바가 같은 규약을 쓴다. 반대로 "펼친 것" 을 담으면 새 채널이 항상 접힌 채로 뜬다.)
     */
    const [collapsedFolders, setCollapsedFolders] = useState(new Set());
    const toggleFolders = (channelId) => setCollapsedFolders(prev => {
        const next = new Set(prev);
        if (next.has(channelId)) next.delete(channelId); else next.add(channelId);
        return next;
    });

    // 즐겨찾기 상태 (localStorage에 저장)
    const [favorites, setFavorites] = useState(() => {
        try {
            const stored = localStorage.getItem("channel_favorites");
            return stored ? new Set(JSON.parse(stored)) : new Set();
        } catch { return new Set(); }
    });

    const toggleFavorite = (e, channelId) => {
        e.stopPropagation();
        setFavorites(prev => {
            const next = new Set(prev);
            if (next.has(channelId)) next.delete(channelId);
            else next.add(channelId);
            try { localStorage.setItem("channel_favorites", JSON.stringify([...next])); } catch {}
            return next;
        });
    };

    const projectsAll     = channels.filter(c => c.type === "PROJECT" && !favorites.has(c.id));
    const projectOngoing  = projectsAll.filter(c => projectPhase(c) === "ongoing");
    const projectUpcoming = projectsAll.filter(c => projectPhase(c) === "upcoming");
    const projectUndated  = projectsAll.filter(c => projectPhase(c) === "undated");
    const projectFinished = projectsAll.filter(c => projectPhase(c) === "finished");
    /**
     * 기타 — 프로젝트가 아닌 모든 채널.
     *
     * <p>{@code type === "OTHER"} 로 좁히지 않는다. 폐지된 공지·팀 채널은 서버 러너가 OTHER 로
     * 전환하지만, 러너가 아직 돌지 않은 DB 에서는 여전히 NOTICE/TEAM 인 채로 온다. 그때
     * 등호 비교였다면 그 채널들이 <b>어느 섹션에도 안 잡혀 화면에서 사라진다</b>(데이터는 살아 있는데
     * 열 방법이 없는 상태). 부정 조건으로 두면 항상 어딘가에는 보인다.
     */
    const otherChannels   = channels.filter(c => c.type !== "PROJECT" && !c.archived && !favorites.has(c.id));

    const favoriteChannels = channels.filter(c => favorites.has(c.id));

    /**
     * 채널 전환 — 열려 있던 폴더 트리를 <b>먼저 접고</b> 이동한다.
     *
     * <p>바로 이동하면 이전 채널의 폴더 목록이 그 자리에서 사라지고(새 채널엔 폴더가 없거나 다르므로)
     * 사이드바 높이가 툭 튀어 "딱딱 끊기는" 느낌이 난다. 접기 애니메이션(180ms)이 끝난 뒤 이동하면
     * 목록이 스르륵 닫히고 새 채널로 넘어간다.
     *
     * <p>{@code collapsedFolders} 를 쓰지 않고 별도 상태를 두는 이유: 그 집합은 <b>사용자가 접어 둔</b>
     * 채널을 기억하는 값이다. 전환용으로 재사용하면 돌아왔을 때 접힌 채로 남아, 사용자가 접지도
     * 않은 폴더가 닫혀 있게 된다.
     */
    const [closingChannelId, setClosingChannelId] = useState(null);
    const closeTimer = useRef(null);

    useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);

    const handleSelectChannel = (id) => {
        const treeWasOpen = selectedId != null && id !== selectedId
            && folderData.folders.length > 0 && !collapsedFolders.has(selectedId);

        /**
         * 고른 채널의 폴더 목록은 <b>자동으로 펼친다</b> — 채널을 여는 목적이 그 안을 보는 것이라
         * 접힌 채로 두면 한 번 더 눌러야 한다. 예전에 사용자가 접어 뒀더라도 새로 진입할 때는 연다.
         */
        const openTarget = () => setCollapsedFolders(prev => {
            if (!prev.has(id)) return prev;          // 이미 열림 — 새 Set 을 만들어 리렌더를 늘리지 않는다
            const next = new Set(prev);
            next.delete(id);
            return next;
        });

        if (!treeWasOpen) { openTarget(); onSelect(id); onSelectFolder(ALL); return; }

        setClosingChannelId(selectedId);
        if (closeTimer.current) clearTimeout(closeTimer.current);
        // 접힘 전이(180ms)보다 살짝 뒤 — 애니메이션이 끝난 프레임에서 이동해야 중간에 끊기지 않는다.
        closeTimer.current = setTimeout(() => {
            setClosingChannelId(null);
            openTarget();
            onSelect(id);
            onSelectFolder(ALL);
        }, 200);
    };

    /**
     * 하위 컴포넌트에 넘길 값 묶음.
     *
     * <p>{@code ChannelItem}·{@code FolderTree} 는 <b>모듈 최상위</b>에 있어야 접기 애니메이션과
     * ⋮ 메뉴 state 가 유지된다(파일 상단 주석 참고). 그래서 클로저로 값을 가져다 쓸 수 없고
     * 이렇게 한 덩어리로 전달한다 — 인자를 하나씩 늘리면 호출부 세 곳을 매번 같이 고쳐야 한다.
     */
    const ctx = useMemo(() => ({
        selectedId, selectedFolder, hasNewMap, favorites, folderData, collapsedFolders,
        closingChannelId,
        // 채널 클릭은 전환 애니메이션을 타야 하므로 onSelect 를 직접 넘기지 않는다.
        onSelect: handleSelectChannel, onSelectFolder, onManageFolders, toggleFavorite, toggleFolders,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [selectedId, selectedFolder, hasNewMap, favorites, folderData, collapsedFolders,
        closingChannelId, onSelect, onSelectFolder, onManageFolders]);

    return (
        <Box sx={{
            width: 250, flexShrink: 0,
            // 두 pane(목록 | 내용)을 가르는 선 하나만 남긴다 — 여기서 지우면 목록과 본문이 붙어 보인다.
            borderRight: `1px solid ${C.border}`,
            height: "100%",
            display: "flex", flexDirection: "column",
            bgcolor: C.surface,
        }}>
            {/* 헤더 — 제목 아래 Divider 를 뺐다. 바로 아래 섹션 헤더가 이미 경계를 만들어 선이 겹쳤다. */}
            <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography sx={{ ...ds.typography.pageTitle, fontSize: 17 }}>채널</Typography>
                    <ActionButton variant="add"
                        startIcon={<AddRoundedIcon sx={{ fontSize: 15 }} />}
                        onClick={onCreateOpen}
                        sx={{ fontSize: 13, py: 0.5 }}
                        label="추가" />
                </Stack>
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto" }}>
                {/* 즐겨찾기 섹션 */}
                {favoriteChannels.length > 0 && (
                    <>
                        <Box
                            onClick={() => setShowFavorites(v => !v)}
                            sx={{
                                display: "flex", alignItems: "center", px: 2, pt: 2, pb: 0.5,
                                cursor: "pointer", "&:hover": { opacity: 0.75 },
                            }}
                        >
                            {showFavorites
                                ? <ExpandLessRoundedIcon sx={{ fontSize: 13, color: C.textMuted, mr: 0.3 }} />
                                : <ExpandMoreRoundedIcon sx={{ fontSize: 13, color: C.textMuted, mr: 0.3 }} />}
                            <StarRoundedIcon sx={{ fontSize: 12, color: "#f59e0b", mr: 0.5 }} />
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: "0.5px",
                                textTransform: "uppercase", flex: 1 }}>
                                즐겨찾기
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: "#cbd5e1", fontWeight: 600 }}>
                                {favoriteChannels.length}
                            </Typography>
                        </Box>
                        {showFavorites && favoriteChannels.map(c => (
                            <React.Fragment key={c.id}>
                                <ChannelItem ctx={ctx} channel={c} inFavorites />
                                <FolderTree ctx={ctx} channel={c} />
                            </React.Fragment>
                        ))}
                    </>
                )}

                {projectsAll.length > 0 && favoriteChannels.length > 0 && <TypeBoundary />}

                {projectsAll.length > 0 && (() => {
                    /**
                     * 프로젝트 — 단일 상위 섹션 + 4개 세부 토글 (진행중 / 예정 / 일정 미지정(대기) / 종료).
                     * 기본 활성: 진행중·예정·일정 미지정 = 열림 / 종료 = 닫힘.
                     *
                     * <h4>표시 규칙</h4>
                     * <ul>
                     *   <li>{@code alwaysShowHeader} — list 가 0건이어도 헤더 표시 (종료된 프로젝트 항상 노출용)</li>
                     *   <li>토글이 닫혀도 현재 선택된 채널이 list 안에 있으면 그 채널은 강제로 노출</li>
                     * </ul>
                     */
                    const subSections = [
                        { key: "ongoing",  label: "진행중",          dot: "#34d399", list: projectOngoing,  open: showOngoingProj,  setOpen: setShowOngoingProj,  dimmed: false, alwaysShowHeader: false },
                        { key: "upcoming", label: "예정",            dot: "#a78bfa", list: projectUpcoming, open: showUpcomingProj, setOpen: setShowUpcomingProj, dimmed: false, alwaysShowHeader: false },
                        { key: "undated",  label: "일정 미지정(대기)", dot: "#94a3b8", list: projectUndated,  open: showUndatedProj,  setOpen: setShowUndatedProj,  dimmed: true,  alwaysShowHeader: false },
                        { key: "finished", label: "종료",            dot: "#f87171", list: projectFinished, open: showArchivedProj, setOpen: setShowArchivedProj, dimmed: true,  alwaysShowHeader: true },
                    ];
                    const visibleSubs = subSections.filter(s => s.alwaysShowHeader || s.list.length > 0);
                    if (visibleSubs.length === 0) return null;
                    return (
                        <>
                            <SectionHeader label="프로젝트" count={projectsAll.length} />
                            {visibleSubs.map((sub, subIdx) => {
                                // 토글이 닫혀있어도 현재 선택된 채널은 강제 노출
                                const itemsToRender = sub.open
                                    ? sub.list
                                    : sub.list.filter(c => c.id === selectedId);
                                return (
                                    <Box key={sub.key}>
                                        {/*
                                          * 구간 사이 구분선 — 진행중/예정/일정 미지정/종료는 <b>서로 다른 상태</b>라
                                          * 붙여 놓으면 한 덩어리로 읽힌다. 여백 + 옅은 선으로 "여기서 상태가 바뀐다" 를
                                          * 눈에 끊어 준다. 첫 구간 위에는 이미 '프로젝트' 헤더가 있어 선을 넣지 않는다.
                                          *
                                          * 선만 안쪽으로 들이고(mx) 구간 자체는 사이드바 폭을 그대로 쓴다 —
                                          * 구간에 여백을 주면 선택된 채널의 좌측 accent bar 가 pane 가장자리에서 떨어진다.
                                          */}
                                        {subIdx > 0 && (
                                            <Divider sx={{ mx: 2, mt: 2.25, mb: 1.25, borderColor: C.borderLight }} />
                                        )}
                                        <Box
                                            onClick={() => sub.setOpen(v => !v)}
                                            sx={{
                                                display: "flex", alignItems: "center", gap: 0.7,
                                                // 헤더는 들여쓰지 않는다 — 들여쓰기는 아래 채널 항목만 갖는다.
                                                px: 2, py: 0.55, mb: 0.4, cursor: "pointer",
                                                "&:hover": { bgcolor: C.bg },
                                            }}
                                        >
                                            {/* 상태를 <b>색점</b> 대신 <b>색 폴더</b>로 — 이 줄이 곧 묶음(폴더)이라는 뜻이
                                                아이콘 모양에서 바로 읽히고, 6px 점보다 색도 훨씬 잘 보인다. */}
                                            <FolderRoundedIcon sx={{ fontSize: 16, color: sub.dot, flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: 12.5, color: C.textSecondary, fontWeight: 700, flex: 1, minWidth: 0 }}>
                                                {sub.label}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, color: "#cbd5e1", fontWeight: 600 }}>
                                                {sub.list.length}
                                            </Typography>
                                            {/* 토글은 오른쪽 끝 — 라벨의 왼쪽 기준선이 밀리지 않게. */}
                                            {sub.open
                                                ? <ExpandLessRoundedIcon sx={{ fontSize: 14, color: C.textMuted }} />
                                                : <ExpandMoreRoundedIcon sx={{ fontSize: 14, color: C.textMuted }} />}
                                        </Box>
                                        {itemsToRender.map(c => (
                                            <React.Fragment key={c.id}>
                                                <ChannelItem ctx={ctx} channel={c} dimmed={sub.dimmed} indented />
                                                <FolderTree ctx={ctx} channel={c} />
                                            </React.Fragment>
                                        ))}
                                    </Box>
                                );
                            })}
                        </>
                    );
                })()}

                {/* (제거됨) '공지' · '팀' 섹션 — 공지는 사내 게시판으로, 팀 채널은 폐지.
                    두 유형의 옛 채널은 아래 '기타' 에 모여 계속 열람된다. */}

                {otherChannels.length > 0 && (projectsAll.length > 0 || favoriteChannels.length > 0) && (
                    <TypeBoundary />
                )}

                {otherChannels.length > 0 && (
                    <>
                        <SectionHeader label="기타" count={otherChannels.length} />
                        {otherChannels.map(c => (
                            <React.Fragment key={c.id}>
                                <ChannelItem ctx={ctx} channel={c} />
                                <FolderTree ctx={ctx} channel={c} />
                            </React.Fragment>
                        ))}
                    </>
                )}

                {channels.length === 0 && (
                    <Box sx={{ textAlign: "center", pt: 6, color: C.textMuted }}>
                        <FolderSpecialRoundedIcon sx={{ fontSize: 36, mb: 1, opacity: 0.4 }} />
                        <Typography sx={{ fontSize: 13 }}>채널이 없습니다</Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default ChannelSidebar;
